export interface FirebirdConfig {
  apiKey: string;
  campaignSlug: string;
  baseUrl?: string;
}

export interface CaptureEmailParams {
  email: string;
  name?: string;
  phone?: string;
  country?: string;
  zip?: string;
  city?: string;
  /**
   * Texts of the campaign consent items the fan checked (from
   * `campaign.consent.items[].text`). Pass `[]` if the items were shown
   * and none were checked; omit if consent items were not displayed.
   */
  consentSelections?: string[];
}

export interface CaptureEmailResponse {
  userId: string;
  isNew: boolean;
}

export interface ConnectSpotifyOptions {
  onSuccess?: (result: AuthResult) => void;
  onError?: (error: FirebirdError) => void;
  returnUrl?: string;
  /**
   * Texts of the campaign consent items the fan checked (from
   * `campaign.consent.items[].text`). Recorded against the fan once
   * authentication completes.
   */
  consentSelections?: string[];
}

export type ConnectAppleOptions = ConnectSpotifyOptions;

export interface ConnectAppleMusicOptions {
  /** Required: email identifies the fan since Apple Music doesn't share one. */
  email: string;
  name?: string;
  onSuccess?: (result: AuthResult) => void;
  onError?: (error: FirebirdError) => void;
  /**
   * Texts of the campaign consent items the fan checked (from
   * `campaign.consent.items[].text`). Recorded against the fan once
   * authentication completes.
   */
  consentSelections?: string[];
}

export interface AuthResult {
  token: string;
  user: {
    id: string;
    email: string;
    name: string;
  };
}

// ── Spotify playlists ──────────────────────────────────────────────────────

export interface CreateSpotifyPlaylistParams {
  name: string;
  description?: string;
  isPublic?: boolean;
  /** Bare Spotify track IDs (no `spotify:track:` prefix). */
  tracks?: string[];
}

export interface CreateSpotifyPlaylistResult {
  status: 'created' | 'error';
  playlistId: string;
  /** Public open.spotify.com URL. */
  externalUrl: string;
  addedTracks: number;
  failedTracks: string[];
}

// ── Apple Music playlists ─────────────────────────────────────────────────

export interface CreateAppleMusicPlaylistParams {
  name: string;
  description?: string;
  /** Apple Music catalog song IDs (numeric strings, e.g. `1440857781`). */
  tracks?: string[];
}

export interface CreateAppleMusicPlaylistResult {
  status: 'created' | 'error';
  playlistId: string;
  /** Apple Music library playlists have no public URL. */
  externalUrl: null;
  addedTracks: number;
  failedTracks: string[];
}

// ── Shared (add-tracks shape is identical per provider) ───────────────────

export interface AddTracksToPlaylistParams {
  playlistId: string;
  tracks: string[];
}

export interface AddTracksToPlaylistResult {
  addedTracks: number;
  failedTracks: string[];
}

export interface SubmitLinkResponse {
  status: string;
  id: number | string;
}

export interface ActionStatus {
  presave: string | null;
  /** Resolves to whichever provider the user signed in with (Spotify or Apple Music). */
  followArtist: string | null;
  /** Resolves to whichever provider the user signed in with (Spotify or Apple Music). */
  followPlaylist: string | null;
  followAppleMusicArtist: string | null;
  followAppleMusicPlaylist: string | null;
  /** `null` when the link submission reward is not enabled on this campaign. */
  linkSubmissions: { count: number; maxPerFan: number } | null;
}

export interface CampaignConfig {
  name: string;
  slug: string;
  signInOptions: string[];
  theme: {
    base: string | null;
    primaryColor: string | null;
    secondaryColor: string | null;
    backgroundColor: string | null;
    textColor: string | null;
    footerTheme: string | null;
  };
  formFields: Record<
    string,
    {
      enabled: boolean;
      required: boolean;
      label?: string;
      placeholder?: string;
    }
  >;
  rewards: {
    release: {
      name: string | null;
      releaseType: string;
      isLive: boolean;
      appleMusicIsLive: boolean;
    } | null;
    followArtistId: string | null;
    followPlaylistId: string | null;
    followAppleMusicArtistId: string | null;
    followAppleMusicPlaylistId: string | null;
    /** `null` when the link submission reward is not enabled on this campaign. */
    linkSubmission: {
      label: string;
      helpText: string | null;
      placeholder: string | null;
      maxPerFan: number;
    } | null;
  };
  generalSettings: {
    logoUrl: string | null;
    featuredImageUrl: string | null;
  };
  consent: {
    /** Where to render the checkboxes relative to the sign-in buttons. */
    position: 'above' | 'below';
    items: ConsentItem[];
  };
}

export interface ConsentItem {
  /** Display text — also the identifier passed back in `consentSelections`. */
  text: string;
  /** Fan must check this item before proceeding. */
  required: boolean;
  /** Render the checkbox pre-checked. */
  prefilled: boolean;
  /** Optional "Learn more" pop-up content, pre-rendered as HTML. */
  learnMoreHtml: string | null;
}

export interface ApiSuccessResponse<T> {
  data: T;
  meta: { requestId: string };
}

export interface ApiErrorResponse {
  error: {
    code: string;
    message: string;
    details?: { field?: string; message: string }[];
  };
}

export type FirebirdEvent =
  | 'ready'
  | 'auth:success'
  | 'auth:error'
  | 'capture:success'
  | 'capture:error';

export class FirebirdError extends Error {
  code: string;
  details?: { field?: string; message: string }[];

  constructor(
    code: string,
    message: string,
    details?: { field?: string; message: string }[]
  ) {
    super(message);
    this.name = 'FirebirdError';
    this.code = code;
    this.details = details;
  }
}
